import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { SMART_SEARCH_KEY } from '../constants/smartSearchKey';
import type { EagGridStore } from '../store/createEagGridStore';
import type { GetDataQueryResponse } from '../types/api/data';
import type { Filters } from '../types/Filters';
import type { QueryFilterObject } from '../types/QueryFilterObject';
import { isOnlyOrderChanged } from '../utils/isOnlyOrderChanged';
import { normalizeFilterValues } from '../utils/normalizeFilterValues';
export type UseFilterHydrationParams = {
  dataQuery: GetDataQueryResponse | null | undefined;
  queryModifier: ((filter: QueryFilterObject) => QueryFilterObject) | undefined;
  initialSmartSearch: string | undefined;
  store: Pick<EagGridStore, 'setStaticFilters' | 'setFilterValues'>;
};

/**
 * Watches `dataQuery` and re-seeds `filterValues` in the store whenever the
 * server delivers a new data query (initial load, preset switch, query-id fallback).
 *
 * Two guards prevent spurious re-seeding:
 *   Guard 1 — exact same object reference: no-op (React double-invoke safety).
 *   Guard 2 — only `order` changed (`setColumnSort` preserves `.filters` reference):
 *             skips `setFilterValues` to avoid a duplicate `createDataQuery` POST.
 *
 * Returns `isFilterHydrated` — becomes `true` after the first successful seed.
 */
export function useFilterHydration(params) {
  const $ = _c(7);
  const [isFilterHydrated, setIsFilterHydrated] = useState(false);
  const prevDataQueryRef = useRef(null);
  let t0;
  if ($[0] !== params) {
    t0 = () => {
      if (!params.dataQuery) {
        return;
      }
      const prevDataQuery = prevDataQueryRef.current;
      if (prevDataQuery === params.dataQuery) {
        return;
      }
      prevDataQueryRef.current = params.dataQuery;
      if (isOnlyOrderChanged(prevDataQuery, params.dataQuery)) {
        return;
      }
      const staticFilters = params.queryModifier ? params.queryModifier({}) : {};
      const smartSearchValue = params.initialSmartSearch ?? params.dataQuery.smartSearch;
      const newInitialFilters = {
        ...normalizeFilterValues(params.dataQuery.filters),
        ...(smartSearchValue ? {
          [SMART_SEARCH_KEY]: {
            [SMART_SEARCH_KEY]: smartSearchValue
          }
        } : {})
      };
      params.store.setStaticFilters(staticFilters);
      params.store.setFilterValues(newInitialFilters);
      setIsFilterHydrated(true);
    };
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== params.dataQuery || $[3] !== params.initialSmartSearch || $[4] !== params.queryModifier || $[5] !== params.store) {
    t1 = [params.dataQuery, params.queryModifier, params.initialSmartSearch, params.store];
    $[2] = params.dataQuery;
    $[3] = params.initialSmartSearch;
    $[4] = params.queryModifier;
    $[5] = params.store;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  useEffect(t0, t1);
  return isFilterHydrated;
}